import React, { createContext, useContext, useState, useCallback } from 'react';

export interface ViewerMediaItem {
  url: string;
  mediaType: 'image' | 'video';
}

interface FullscreenViewerContextType {
  isOpen: boolean;
  mediaItems: ViewerMediaItem[];
  currentIndex: number;
  openViewer: (items: ViewerMediaItem[], startIndex?: number) => void;
  closeViewer: () => void;
  goTo: (index: number) => void;
  goNext: () => void;
  goPrev: () => void;
}

const FullscreenViewerContext = createContext<FullscreenViewerContextType | undefined>(undefined);

export function FullscreenViewerProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState<ViewerMediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openViewer = useCallback((items: ViewerMediaItem[], startIndex?: number) => {
    const validItems = items.filter(
      (item): item is ViewerMediaItem => item.url != null
    );
    if (validItems.length === 0) return;

    setMediaItems(validItems);
    setCurrentIndex(
      startIndex != null ? Math.min(Math.max(0, startIndex), validItems.length - 1) : 0
    );
    setIsOpen(true);
  }, []);

  const closeViewer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const goTo = useCallback((index: number) => {
    setCurrentIndex((prev) => {
      if (index < 0 || index >= mediaItems.length) return prev;
      return index;
    });
  }, [mediaItems.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= mediaItems.length - 1) return prev;
      return prev + 1;
    });
  }, [mediaItems.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev <= 0) return prev;
      return prev - 1;
    });
  }, []);

  return (
    <FullscreenViewerContext.Provider
      value={{
        isOpen,
        mediaItems,
        currentIndex,
        openViewer,
        closeViewer,
        goTo,
        goNext,
        goPrev,
      }}
    >
      {children}
    </FullscreenViewerContext.Provider>
  );
}

export function useFullscreenViewer() {
  const context = useContext(FullscreenViewerContext);
  if (!context) {
    throw new Error('useFullscreenViewer must be used within a FullscreenViewerProvider');
  }
  return context;
}
